/**
 * LocationPicker — production address picker backed by Mapbox.
 *
 * Composes AddressAutocomplete (Search Box API) with an interactive draggable
 * pin map (react-map-gl). Public props are unchanged from the previous
 * Leaflet-based implementation so all callers (StoreSignup, Checkout,
 * RideBook, etc.) keep working with no code change.
 */
import { useEffect, useRef, useState } from 'react';
import { Marker, MapRef } from 'react-map-gl';
import MapView from './maps/MapView';
import AddressAutocomplete, { reverseGeocode } from './maps/AddressAutocomplete';
import { DEFAULT_CENTER } from '../config/mapbox';

export interface PickedLocation {
  address: string;
  latitude: number;
  longitude: number;
}

interface Props {
  label?: string;
  placeholder?: string;
  value?: PickedLocation | null;
  onChange: (loc: PickedLocation) => void;
  markerColor?: 'green' | 'red' | 'blue';
  showMap?: boolean;
  height?: number;
  dotColor?: string;
  /**
   * 'inline' (default) — render the embedded Mapbox map under the input.
   * 'none'             — render only the input + suggestions; use when a
   *                      shared parent map (e.g. RideRouteMap) is rendered.
   */
  mapMode?: 'inline' | 'none';
  onFocusField?: () => void;
  active?: boolean;
}

/** Re-exported for backward compat — many callers import from LocationPicker. */
export { reverseGeocode };

const LocationPicker: React.FC<Props> = ({
  label,
  placeholder = 'Search location…',
  value,
  onChange,
  showMap = true,
  height = 220,
  dotColor = '#0C831F',
  mapMode = 'inline',
  onFocusField,
  active = false,
}) => {
  const [pos, setPos] = useState<{ lat: number; lng: number } | null>(
    value ? { lat: value.latitude, lng: value.longitude } : null,
  );
  const [userGps, setUserGps] = useState<{ lat: number; lng: number } | null>(null);
  const mapRef = useRef<MapRef>(null);

  useEffect(() => {
    if (!navigator.geolocation || pos) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setUserGps({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => undefined,
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60_000 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (value && (value.latitude !== pos?.lat || value.longitude !== pos?.lng)) {
      setPos({ lat: value.latitude, lng: value.longitude });
      mapRef.current?.flyTo({ center: [value.longitude, value.latitude], zoom: 14, duration: 600 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.latitude, value?.longitude]);

  const center = pos
    ? { latitude: pos.lat, longitude: pos.lng }
    : userGps
      ? { latitude: userGps.lat, longitude: userGps.lng }
      : DEFAULT_CENTER;

  const handleSelect = (r: { address: string; latitude: number; longitude: number }) => {
    setPos({ lat: r.latitude, lng: r.longitude });
    onChange(r);
    mapRef.current?.flyTo({ center: [r.longitude, r.latitude], zoom: 14, duration: 600 });
  };

  const pickCoord = async (lat: number, lng: number) => {
    setPos({ lat, lng });
    const address = await reverseGeocode(lat, lng);
    onChange({ address, latitude: lat, longitude: lng });
  };

  const handleUseMyLocation = () => {
    onFocusField?.();
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => pickCoord(p.coords.latitude, p.coords.longitude),
      () => undefined,
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  return (
    <div style={{ marginBottom: 8 }}>
      {label && (
        <label style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6, display: 'block' }}>
          {label}
        </label>
      )}
      <AddressAutocomplete
        value={value?.address || ''}
        placeholder={placeholder}
        proximity={pos ? { latitude: pos.lat, longitude: pos.lng } : userGps ? { latitude: userGps.lat, longitude: userGps.lng } : undefined}
        onSelect={(r) => { onFocusField?.(); handleSelect(r); }}
        onUseMyLocation={handleUseMyLocation}
        active={active}
        dotColor={dotColor}
      />
      {showMap && mapMode === 'inline' && (
        <div style={{ borderRadius: 12, overflow: 'hidden', marginTop: 8, border: '1px solid #E8E8E8' }}>
          <MapView
            ref={mapRef}
            latitude={center.latitude}
            longitude={center.longitude}
            zoom={pos ? 14 : 11}
            height={height}
            onClick={(e) => pickCoord(e.lngLat.lat, e.lngLat.lng)}
          >
            {pos && (
              <Marker
                latitude={pos.lat}
                longitude={pos.lng}
                draggable
                anchor="bottom"
                color={dotColor}
                onDragEnd={(e) => pickCoord(e.lngLat.lat, e.lngLat.lng)}
              />
            )}
          </MapView>
          <div style={{ fontSize: 11, color: '#888', padding: '6px 10px', background: '#fafafa' }}>
            Tap the map or drag the pin to fine-tune the location.
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
